import { useTranslation } from "react-i18next";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import type {
  ColumnarTable,
  LineChartConfig,
} from "@stockline/shared/types/bi";
import { cn } from "@/lib/utils";
import { Badge } from "@stockline/ui/components/badge";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/shadcn/chart";
import { toChartNumber } from "@stockline/shared/validation/bi/numeric";
import { makeCategoryFormatter, formatValue, prettifyColumn } from "./format";
import { truncateTick } from "./truncate";
import type { ChartVariant } from "./types";

interface LineChartRendererProps {
  table: ColumnarTable;
  config: LineChartConfig;
  title?: string;
  /** Fixed pixel height for the "default" (chat) variant, whose parent has no
   *  definite height. Ignored by the "card" variant, which fills its card. */
  height?: number;
  /** "card" drops axis-name labels and tightens margins for dashboard cards,
   *  and fills the card body (the card owns the height) so resizing reflows the
   *  chart instead of cropping it; "default" keeps the full chat layout. */
  variant?: ChartVariant;
  /** Enter animation. Off for an off-screen export capture: the line's reveal
   *  animation is a clip that html2canvas grabs in its pre-reveal (invisible)
   *  state, so an animated line rasterizes blank. Static → captures correctly. */
  animate?: boolean;
}

export function LineChartRenderer({
  table,
  config,
  title,
  height = 300,
  variant = "default",
  animate = true,
}: LineChartRendererProps) {
  const { t } = useTranslation();

  // Get column indices. We key the chart data and config by a synthetic
  // `series-N` slug (CSS-identifier safe) so `var(--color-series-N)` is always
  // valid even when raw column names contain spaces or other invalid chars.
  const xIdx = table.columns.indexOf(config.x_column);
  const yIndices = config.y_columns.map((col, i) => ({
    key: `series-${i}`,
    label: prettifyColumn(col),
    idx: table.columns.indexOf(col),
  }));

  // No rows, OR the x column / any y-series column no longer exists in the
  // result (name drift → `indexOf` is -1). Either way there is nothing
  // meaningful to plot, so show the placeholder rather than a chart that
  // silently reads undefined cells.
  if (
    !table?.data ||
    table.data.length === 0 ||
    xIdx === -1 ||
    yIndices.some(({ idx }) => idx === -1)
  ) {
    return (
      <div className="text-muted-foreground text-sm p-4">
        {t("charts.noData", "No data to display")}
      </div>
    );
  }

  const card = variant === "card";
  const xLabel = prettifyColumn(config.x_column);
  const showLegend = yIndices.length > 1;

  const formatX = makeCategoryFormatter(table.data.map((row) => row[xIdx]));
  const chartData = table.data.map((row) => {
    const point: Record<string, string | number | null> = {
      x: formatX(row[xIdx]),
    };
    for (const { key, idx } of yIndices) {
      // pg numeric/int8 arrive as strings; coerce so recharts scales the axis
      // numerically instead of plotting a flat/categorical line. A missing cell
      // (no entries for that bucket — e.g. a product with no sales that month)
      // becomes 0 via `toChartNumber(null)` so the line stays continuous
      // instead of breaking into gaps and restarting.
      point[key] = toChartNumber(row[idx]);
    }
    return point;
  });

  const chartConfig = Object.fromEntries(
    yIndices.map(({ key, label }, i) => [
      key,
      { label, color: `hsl(var(--chart-${(i % 5) + 1}))` },
    ]),
  ) satisfies ChartConfig;

  // Sum each y-series across the displayed period. `chartData` already holds
  // the coerced numeric values (toChartNumber), so a straight reduce mirrors
  // exactly what the plotted line shows — no re-parsing the raw table. Each
  // total is tagged with its series' own chart color so the pill visually
  // ties back to its line (and the legend, when shown).
  const totals =
    config.show_total &&
    yIndices.map(({ key, label }) => ({
      key,
      label,
      color: chartConfig[key].color,
      sum: chartData.reduce(
        (acc, point) => acc + (typeof point[key] === "number" ? point[key] : 0),
        0,
      ),
    }));

  return (
    // Card variant is a flex column so the totals badge block + chart share the
    // card height (chart takes the remaining space via `flex-1`), instead of the
    // chart being `h-full` beside the badge sibling and overflowing the card.
    <div className={cn("space-y-2", card ? "flex h-full flex-col" : "h-full")}>
      {/* The card variant fills its height and never shows its own title (the
          WidgetCard header owns it); rendering one here would both duplicate
          the title and overflow the h-full chart div below. */}
      {title && !card && <h4 className="font-medium text-sm">{title}</h4>}
      {/* Unlike the title, the total is useful regardless of context so it
          renders in BOTH the card and default variants. */}
      {totals && (
        <div className="flex flex-wrap gap-1.5">
          {totals.map(({ key, label, color, sum }) => (
            <Badge
              key={key}
              variant="outline"
              className="border-transparent font-medium"
              style={{
                backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
                color,
              }}
            >
              {showLegend
                ? `${t("charts.total", "Total")} ${label}: `
                : `${t("charts.total", "Total")}: `}
              {formatValue(sum, config.unit)}
            </Badge>
          ))}
        </div>
      )}
      <div
        className={cn(
          card ? "min-h-0 w-full flex-1" : "w-full",
          // Axes/grid blur in softly; the LINE draws in L→R via a CSS clip on
          // the line layer (recharts' own JS draw no-ops on a 0×0 board mount).
          // `animate` is isNew → fires on creation AND update.
          animate && "animate-line-draw animate-axes-in",
        )}
        style={card ? undefined : { height }}
      >
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-full w-full"
        >
          <LineChart
            data={chartData}
            margin={
              // Rotated, all-categories x-axis labels need vertical room below
              // the plot so they aren't clipped. The card variant keeps a
              // tighter footprint than the expanded/default one.
              card
                ? { top: 8, right: 16, left: 0, bottom: 28 }
                : { top: 20, right: 30, left: 20, bottom: 56 }
            }
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="x"
              tickLine={false}
              axisLine={false}
              tickMargin={3}
              tick={{ fontSize: card ? 10 : 11 }}
              // Render EVERY category (interval=0) and angle the labels so long
              // names don't collide or get silently dropped. `height` must clear
              // the diagonal extent of a 16-char label at -45° or the labels
              // spill past the card's bottom edge; the bottom margin adds room.
              interval={0}
              angle={-45}
              textAnchor="end"
              height={card ? 72 : 96}
              tickFormatter={(value: string) => truncateTick(value)}
              label={
                card || !xLabel
                  ? undefined
                  : { value: xLabel, position: "bottom", offset: 8 }
              }
            />
            <YAxis
              // A unit prefix widens every tick (e.g. "kg1.2K"), so give the
              // card-mode axis a little more room to avoid clipping into the plot.
              // The default branch MUST stay a concrete number (recharts' own
              // default is 60) — NEVER `undefined`. recharts builds the axis map
              // as `{...YAxis.defaultProps, ...child.props}`, and React 19's JSX
              // runtime no longer drops an explicit `undefined`, so `width:
              // undefined` clobbers the default 60 → `offset.left` becomes NaN →
              // the whole plot width is NaN and nothing renders.
              width={card ? (config.unit ? 52 : 40) : 60}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: card ? 11 : 12 }}
              tickFormatter={(v: number) =>
                formatValue(v, config.unit, { compact: true })
              }
              label={
                card || showLegend || !yIndices.length
                  ? undefined
                  : {
                      value: yIndices[0].label,
                      angle: -90,
                      position: "insideLeft",
                    }
              }
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  valueFormatter={(v) => formatValue(Number(v), config.unit)}
                />
              }
            />
            {/* Legend sits at the top so it never collides with the bottom
                x-axis name label (default) or the x ticks (card). */}
            {showLegend && (
              <ChartLegend
                verticalAlign="top"
                content={<ChartLegendContent />}
              />
            )}
            {yIndices.map(({ key, label }) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={label}
                stroke={`var(--color-${key})`}
                strokeWidth={2}
                dot={{ fill: `var(--color-${key})`, strokeWidth: 2 }}
                activeDot={{ r: 6 }}
                // recharts' JS draw is disabled — unreliable on a 0×0 board
                // mount. The CSS L→R clip (`animate-line-draw`) on the wrapper
                // is the entrance instead.
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ChartContainer>
      </div>
    </div>
  );
}
